import WhatsAppService from '../../../../../../services/whatsapp.service';
import whatsappConfig from '../../../../../../config/whatsapp.config';

const { messageTemplates: MESSAGE_TEMPLATES } = whatsappConfig;

/**
 * Service functions for WhatsApp chat integration
 */

/**
 * Fetch WhatsApp chat messages
 * @param {string} chatId - The chat ID (phone number or group link)
 * @param {number} count - Number of messages to fetch
 * @param {Function} setLoading - Function to update loading state
 * @param {Function} setError - Function to update error state
 * @param {Function} setChatHistory - Function to update chat history
 * @param {Function} showToast - Function to display toast notifications
 * @param {boolean} isLoadingMore - Whether this is a load-more operation
 * @param {string|null} oldestTimestamp - Timestamp of the oldest message for loading more
 * @returns {Promise<void>}
 */
export const fetchMessages = async (
  chatId,
  count = 100,
  setLoading,
  setError,
  setChatHistory,
  showToast,
  isLoadingMore = false,
  oldestTimestamp = null
) => {
  if (!chatId) {
    setError('Invalid chat ID');
    setLoading(false);
    return;
  }
  
  try {
    // Set larger count when loading earlier messages
    const fetchCount = isLoadingMore ? 100 : count;
    
    let response;
    try {
      response = await WhatsAppService.getMessages(chatId, fetchCount, isLoadingMore ? oldestTimestamp : null);
    } catch (apiError) {
      console.error('API error fetching messages:', apiError);
      throw new Error('API error: ' + (apiError.message || 'Failed to fetch messages'));
    }

    // Validate response
    if (!response) {
      throw new Error('Empty response from WhatsApp service');
    }

    // Process messages - ensure it's an array and filter out any invalid messages
    const messages = Array.isArray(response) 
      ? response.filter(msg => msg && (typeof msg === 'object'))
      : [];
      
    console.log(`[WhatsAppIntegration] Received ${messages.length} messages, loading more: ${isLoadingMore}`);
    
    if (messages.length > 0) {
      // Update chat history with deduplication or initial data
      setChatHistory(prev => {
        if (isLoadingMore) {
          // Create a Set of existing message IDs for quick lookup
          const existingIds = new Set(prev.map(msg => msg.id || msg._id));
          
          // Filter out any messages that already exist in the chat history
          const newMessages = messages.filter(msg => {
            const messageId = msg.id || msg._id;
            return !existingIds.has(messageId);
          });
          
          console.log(`[WhatsAppIntegration] Found ${newMessages.length} new messages after deduplication`);
          
          // Add new messages at the beginning of the array (older messages first)
          return [...newMessages, ...prev];
        } else {
          // Initial load
          return messages;
        }
      });
      
      if (isLoadingMore && messages.length > 0) {
        // Only show toast for loading more if we actually found new messages
        void showToast('Chat Updated', 'Loaded earlier messages', 'success');
      }
      return messages;
    } else {
      if (!isLoadingMore) {
        throw new Error('No messages found');
      }
      return [];
    }
  } catch (err) {
    console.error('Error fetching WhatsApp chat:', err);
    
    // Only set error message in UI for empty chats (no toast for these)
    if (!isLoadingMore) {
      if (err.message === 'No messages found') {
        setError('No messages found in this chat');
      } else {
        // For other errors, set error state and show toast
        setError(`Failed to load chat history: ${err.message || 'Unknown error'}`);
        // Show toast only for actual errors, not for empty chats
        void showToast(
          'Error',
          `Could not load WhatsApp chat: ${err.message || 'Please try again later'}`,
          'error'
        );
      }
    } else {
      // For load more errors, just show a toast
      void showToast(
        'Notice',
        'No more earlier messages available',
        'info'
      );
    }
    return [];
  } finally {
    // Always set loading to false
    setLoading(false);
  }
};

/**
 * Send a WhatsApp message
 * @param {string} chatId - The chat ID (phone number or group link)
 * @param {string} message - Message text to send
 * @param {string} senderName - The sender's name to personalize the message
 * @param {Function} setIsSending - Function to update sending state
 * @param {Function} showToast - Function to display toast notifications
 * @param {Function} resetForm - Function to reset the form after sending
 * @param {Function} refreshChat - Function to refresh chat after sending
 * @returns {Promise<boolean>} Success status
 */
export const sendMessage = async (
  chatId,
  message,
  senderName,
  setIsSending,
  showToast,
  resetForm,
  refreshChat
) => {
  if (!chatId || !message.trim()) {
    // Use void to prevent the toast object from being returned
    void showToast('Error', 'Cannot send empty message', 'error');
    return false;
  }
  
  try {
    const personalizedMessage = message.replace(/\{\{senderName\}\}/g, senderName);
    await WhatsAppService.sendTextMessage(chatId, personalizedMessage);
    
    // Reset the input field immediately for better UX
    resetForm();
    
    // Refresh chat history after sending message
    await refreshChat();
    
    // Use void to prevent the toast object from being returned
    void showToast('Message Sent', 'Your message has been sent successfully', 'success');
    return true;
  } catch (error) {
    console.error('Error sending message:', error);
    // Use void to prevent the toast object from being returned
    void showToast('Error', 'Failed to send message', 'error');
    return false;
  } finally {
    setIsSending(false);
  }
};

/**
 * Send a sequence of introduction messages
 * @param {string} chatId - The chat ID (phone number or group link) 
 * @param {string} senderName - The sender's name to personalize the messages
 * @param {Function} setIsSending - Function to update sending state
 * @param {Function} showToast - Function to display toast notifications
 * @param {Function} refreshChat - Function to refresh chat after sending
 * @returns {Promise<boolean>} Success status
 */
export const sendIntroMessages = async (
  chatId,
  senderName,
  setIsSending,
  showToast,
  refreshChat
) => {
  if (!chatId) {
    // Use void to prevent the toast object from being returned
    void showToast('Error', 'Invalid chat ID', 'error');
    return false;
  }
  
  try {
    const sequence = ['intro', 'requirements', 'portfolio'];

    // Send all messages in sequence
    for (const templateId of sequence) {
      const template = MESSAGE_TEMPLATES.find(t => t.id === templateId);
      if (template) {
        const personalizedMessage = template.text.replace(/\{\{senderName\}\}/g, senderName);
        await WhatsAppService.sendTextMessage(chatId, personalizedMessage);
        // Add a small delay between messages to prevent rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    // Refresh chat history and show success
    await refreshChat();
    // Use void to prevent the toast object from being returned
    void showToast('Messages Sent', 'All introduction messages sent successfully', 'success');
    return true;
  } catch (error) {
    console.error('Error sending intro messages:', error);
    // Use void to prevent the toast object from being returned
    void showToast('Error', 'Failed to send introduction messages', 'error');
    return false;
  } finally {
    setIsSending(false);
  }
};
